import {
  Box,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Button,
  Input,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { getVehicules, getUsers } from "../../../api/api";
import GoogleMapsLoader, { useGoogleMaps } from "components/GoogleMapsLoader";
import LoadingSpinner from "components/LoadingSpinner";
import axios from "axios";
import { BASE_URL } from "config/api";
import { useNavigate } from "react-router-dom";
import ambulanceIcon from "assets/img/dashboards/OMNIDOC.png";
import medecinIcon from "assets/img/medecin.png";
import infirmierIcon from "assets/img/infirmier.png";
import config from "config/api";

const GpsMapContent = () => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [medecins, setMedecins] = useState([]);
  const [infirmiers, setInfirmiers] = useState([]);
  const [activeFilter, setActiveFilter] = useState("ambulances");
  const [ambulanceSearch, setAmbulanceSearch] = useState("");
  const [stats, setStats] = useState({
    ambulances: 0,
    medecins: 0,
    infirmiers: 0,
  });
  const markerObjectsRef = useRef(new Map());
  const markerAnimationRefs = useRef([]);
  const infoWindowsRef = useRef(new Map());
  const customOverlaysRef = useRef(new Map());
  const lastMapInstanceRef = useRef(null);
  const { loaded: isGoogleMapsLoaded } = useGoogleMaps();

  // Color mode values - MUST be called before any conditional returns
  const bgColor = useColorModeValue("gray.50", "gray.800");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const normalizedAmbulanceSearch = ambulanceSearch.trim().toLowerCase();
  const filteredVehicles =
    normalizedAmbulanceSearch.length > 0
      ? vehicles.filter((vehicle) =>
          String(vehicle.imatricule || "")
            .toLowerCase()
            .includes(normalizedAmbulanceSearch)
        )
      : vehicles;

  // Classe OverlayView personnalisée pour afficher les labels des ambulances
  class CustomLabelOverlay extends (window.google?.maps?.OverlayView || class {}) {
    constructor(position, content, map) {
      super();
      this.position = position;
      this.content = content;
      this.map = map;
      this.div = null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.innerHTML = this.content;
      this.div = div;
      const panes = this.getPanes();
      panes.overlayMouseTarget.appendChild(div);
    }

    draw() {
      const overlayProjection = this.getProjection();
      const position = overlayProjection.fromLatLngToDivPixel(this.position);
      if (this.div) {
        this.div.style.left = position.x + 'px';
        this.div.style.top = position.y + 'px';
        this.div.style.transform = 'translate(-50%, -100%)';
        this.div.style.marginTop = '-25px';
      }
    }

    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
      }
    }

    setPosition(position) {
      this.position = position;
      this.draw();
    }

    setContent(content) {
      this.content = content;
      if (this.div) {
        this.div.innerHTML = content;
      }
    }
  }

  // Fonction pour masquer le bouton de fermeture et supprimer le fond blanc des InfoWindows
  const hideInfoWindowCloseButton = (infoWindow) => {
    infoWindow.addListener('domready', () => {
      setTimeout(() => {
        // Chercher dans tous les iframes de Google Maps
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (iframeDoc) {
              // Masquer le bouton de fermeture - approche agressive
              const selectors = [
                '.gm-ui-hover-effect', 
                '[aria-label*="Close"]', 
                '[aria-label*="close"]',
                '[aria-label*="Fermer"]',
                '[aria-label*="fermer"]',
                '[title*="Close"]', 
                '[title*="close"]',
                'button[aria-label*="Close"]',
                'button[aria-label*="close"]',
                'button[aria-label*="Fermer"]',
                '.gm-style-iw-tc',
                'div[role="button"]',
                'button',
                '[role="button"]'
              ];
              
              // Masquer tous les éléments qui pourraient être le bouton X
              selectors.forEach(selector => {
                try {
                  const elements = iframeDoc.querySelectorAll(selector);
                  elements.forEach(el => {
                    const text = el.textContent || '';
                    const ariaLabel = el.getAttribute('aria-label') || '';
                    const title = el.getAttribute('title') || '';
                    const className = el.className || '';
                    
                    // Vérifier si c'est un bouton de fermeture
                    if (
                      text.includes('×') || 
                      text.includes('✕') || 
                      text.includes('X') ||
                      text.trim() === '×' ||
                      text.trim() === '✕' ||
                      text.trim() === 'X' ||
                      ariaLabel.toLowerCase().includes('close') ||
                      ariaLabel.toLowerCase().includes('fermer') ||
                      title.toLowerCase().includes('close') ||
                      title.toLowerCase().includes('fermer') ||
                      className.includes('gm-ui-hover-effect') ||
                      className.includes('gm-style-iw-tc')
                    ) {
                      el.style.display = 'none !important';
                      el.style.visibility = 'hidden !important';
                      el.style.opacity = '0 !important';
                      el.style.width = '0 !important';
                      el.style.height = '0 !important';
                      el.style.pointerEvents = 'none !important';
                      el.remove(); // Supprimer complètement l'élément
                    }
                  });
                } catch (e) {
                  // Ignorer les erreurs
                }
              });
              
              // Masquer aussi tous les boutons dans la zone de l'InfoWindow
              const infoWindowContainer = iframeDoc.querySelector('.gm-style-iw-c');
              if (infoWindowContainer) {
                const allButtons = infoWindowContainer.querySelectorAll('button, [role="button"]');
                allButtons.forEach(btn => {
                  const text = btn.textContent || '';
                  if (text.includes('×') || text.includes('✕') || text.includes('X') || text.trim() === '×' || text.trim() === '✕' || text.trim() === 'X') {
                    btn.style.display = 'none !important';
                    btn.style.visibility = 'hidden !important';
                    btn.remove();
                  }
                });
              }
              
              // Supprimer le fond blanc de l'InfoWindow
              const infoWindowDiv = iframeDoc.querySelector('.gm-style-iw-d');
              if (infoWindowDiv) {
                infoWindowDiv.style.background = 'transparent';
                infoWindowDiv.style.padding = '0';
                infoWindowDiv.style.boxShadow = 'none';
              }
              
              const infoWindowParent = iframeDoc.querySelector('.gm-style-iw-c');
              if (infoWindowParent) {
                infoWindowParent.style.background = 'transparent';
                infoWindowParent.style.padding = '0';
                infoWindowParent.style.boxShadow = 'none';
              }
              
              const infoWindowBg = iframeDoc.querySelector('.gm-style-iw');
              if (infoWindowBg) {
                infoWindowBg.style.background = 'transparent';
              }
              
              // Masquer la pointe de l'InfoWindow si nécessaire
              const infoWindowTip = iframeDoc.querySelector('.gm-style-iw-t');
              if (infoWindowTip) {
                infoWindowTip.style.background = 'transparent';
              }
              
              // Ajouter un style global dans l'iframe
              const style = iframeDoc.createElement('style');
              style.textContent = `
                .gm-ui-hover-effect,
                [aria-label*="Close"],
                [aria-label*="close"],
                [aria-label*="Fermer"],
                [aria-label*="fermer"],
                [title*="Close"],
                [title*="close"],
                [title*="Fermer"],
                [title*="fermer"],
                button[aria-label*="Close"],
                button[aria-label*="close"],
                button[aria-label*="Fermer"],
                button[aria-label*="fermer"],
                .gm-style-iw-tc,
                button:contains("×"),
                button:contains("✕"),
                button:contains("X"),
                [role="button"]:contains("×"),
                [role="button"]:contains("✕"),
                [role="button"]:contains("X") {
                  display: none !important;
                  visibility: hidden !important;
                  opacity: 0 !important;
                  width: 0 !important;
                  height: 0 !important;
                  pointer-events: none !important;
                }
                .gm-style-iw-d,
                .gm-style-iw-c,
                .gm-style-iw {
                  background: transparent !important;
                  padding: 0 !important;
                  box-shadow: none !important;
                }
                .gm-style-iw-t {
                  background: transparent !important;
                }
                /* Masquer tous les boutons dans l'InfoWindow */
                .gm-style-iw-c button,
                .gm-style-iw-d button {
                  display: none !important;
                  visibility: hidden !important;
                  opacity: 0 !important;
                  width: 0 !important;
                  height: 0 !important;
                  pointer-events: none !important;
                }
              `;
              if (iframeDoc.head) {
                // Supprimer l'ancien style s'il existe
                const existingStyle = iframeDoc.querySelector('style[data-infowindow-custom]');
                if (existingStyle) {
                  existingStyle.remove();
                }
                style.setAttribute('data-infowindow-custom', 'true');
                iframeDoc.head.appendChild(style);
              }
            }
          } catch (e) {
            // Ignorer les erreurs CORS
          }
        });
      }, 150);
    });
  };

  // Function to fetch staff location (médecin / infirmier) via users/:id/loc
  const fetchStaffLocation = async (userId) => {
    try {
      const res = await axios.get(`${BASE_URL}/users/${userId}/loc`, {
        withCredentials: true,
      });
      const payload = res.data;
      const locVal = (payload && typeof payload === 'object' && (payload.lat || payload.lng || payload.loc)) ? (payload.loc ?? payload) : payload;
      if (!locVal) return null;
      if (typeof locVal === "object" && locVal.lat != null && locVal.lng != null) {
        return { lat: Number(locVal.lat), lng: Number(locVal.lng) };
      }
      if (
        typeof locVal === "string" &&
        /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(locVal.trim())
      ) {
        const [lat, lng] = locVal.split(',').map(s => Number(s.trim()));
        return { lat, lng };
      }
      return null;
    } catch (error) {
      console.error(`[fetchStaffLocation] Erreur pour user ${userId}:`, error);
      return null;
    }
  };

  // Fetch user data
  const fetchUserData = async (userId) => {
    if (!userId) {
      return {
        nom: 'Non assigné',
        prenom: ''
      };
    }

    try {
      const response = await axios.get(`${BASE_URL}/users/${userId}`, {
        withCredentials: true,
      });
      
      return {
        nom: response.data.nom || 'Inconnu',
        prenom: response.data.prenom || 'Inconnu'
      };
    } catch (error) {
      console.error(`Error fetching user data for ID ${userId}:`, error);
      return {
        nom: 'Inconnu',
        prenom: 'Inconnu'
      };
    }
  };

  // Function to fetch vehicle location from Navixy
  const fetchVehicleLocation = async (trackerId) => {
    try {
      const data = {
        // Utiliser le hash depuis la config (variable d'environnement)
        hash: config.navixy.hash,
        tracker_id: trackerId,
      };
      
      const response = await axios.post(
        `${config.navixy.apiUrl}/tracker/get_state`,
        data,
        {
        headers: {
          'Content-Type': 'application/json', 
        },
          // IMPORTANT : ne jamais envoyer les cookies / credentials vers l'API Navixy
          // pour éviter l'erreur CORS "credentials mode is include"
          withCredentials: false,
        }
      );

      if (response.data?.state?.gps?.location) {
        return {
          lat: response.data.state.gps.location.lat,
          lng: response.data.state.gps.location.lng
        };
      }
      
      // Si pas de nouvelle position, chercher la dernière position connue dans le tableau vehicles
      const vehicle = vehicles.find(v => v.tracker === trackerId);
      if (vehicle && vehicle.position) {
        return vehicle.position;
      }
      
      // Si aucune position n'est trouvée, retourner la position par défaut
      return {
        lat: 33.5939313, 
        lng: -7.6387418
      };
    } catch (error) {
      // En cas d'erreur 403 ou autre, chercher la dernière position connue dans le tableau vehicles
      const vehicle = vehicles.find(v => v.tracker === trackerId);
      if (vehicle && vehicle.position) {
        return vehicle.position;
      }
      return {
        lat: 33.5939313, 
        lng: -7.6387418
      };
    }
  };

  // Function to fetch vehicles data
  const fetchVehicles = async () => {
    try {
      const response = await getVehicules();

      // Transform and store the required data
      const formattedVehicles = await Promise.all(response.data.map(async (vehicle) => {
        try {
          const userInfo = await fetchUserData(vehicle.user_id);
          const location = await fetchVehicleLocation(vehicle.tracker_id);
          
          return {
            id: vehicle.id,
            user_id: vehicle.user_id,
            nom: userInfo.nom,
            prenom: userInfo.prenom,
            imatricule: vehicle.immatriculation,
            statut: vehicle.statut,
            tracker: vehicle.tracker_id,
            position: location
          };
        } catch (error) {
          console.error(`Error processing vehicle ${vehicle.id}:`, error);
          return null;
        }
      }));

      // Filter out any null values from failed processing
      const validVehicles = formattedVehicles.filter(vehicle => vehicle !== null);
      
      setVehicles(validVehicles);
      
      // Update stats
      setStats(prev => ({
        ...prev,
        ambulances: validVehicles.length,
      }));
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => {
    if (isGoogleMapsLoaded && window.google?.maps && mapRef.current && !mapRef.current._google_map_instance) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        // center: { lat: 33.5939313, lng: -7.6387418 },
        center: { lat: 34.020882, lng: -6.841650 } ,
        zoom: 12,
        styles: [
          {
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#ebe3cd"
              }
            ]
          },
          {
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#523735"
              }
            ]
          },
          {
            "elementType": "labels.text.stroke",
            "stylers": [
              {
                "color": "#f5f1e6"
              }
            ]
          },
          {
            "featureType": "administrative",
            "elementType": "geometry.stroke",
            "stylers": [
              {
                "color": "#c9b2a6"
              }
            ]
          },
          {
            "featureType": "administrative.land_parcel",
            "elementType": "geometry.stroke",
            "stylers": [
              {
                "color": "#dcd2be"
              }
            ]
          },
          {
            "featureType": "administrative.land_parcel",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#ae9e90"
              }
            ]
          },
          {
            "featureType": "landscape.natural",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#dfd2ae"
              }
            ]
          },
          {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#dfd2ae"
              }
            ]
          },
          {
            "featureType": "poi",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#93817c"
              }
            ]
          },
          {
            "featureType": "poi.park",
            "elementType": "geometry.fill",
            "stylers": [
              {
                "color": "#006133"
              }
            ]
          },
          {
            "featureType": "poi.park",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#447530"
              }
            ]
          },
          {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#f5f1e6"
              }
            ]
          },
          {
            "featureType": "road.arterial",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#fdfcf8"
              }
            ]
          },
          {
            "featureType": "road.highway",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#f8c967"
              }
            ]
          },
          {
            "featureType": "road.highway",
            "elementType": "geometry.stroke",
            "stylers": [
              {
                "color": "#e9bc62"
              }
            ]
          },
          {
            "featureType": "road.highway.controlled_access",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#e98d58"
              }
            ]
          },
          {
            "featureType": "road.highway.controlled_access",
            "elementType": "geometry.stroke",
            "stylers": [
              {
                "color": "#db8555"
              }
            ]
          },
          {
            "featureType": "road.local",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#806b63"
              }
            ]
          },
          {
            "featureType": "transit.line",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#dfd2ae"
              }
            ]
          },
          {
            "featureType": "transit.line",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#8f7d77"
              }
            ]
          },
          {
            "featureType": "transit.line",
            "elementType": "labels.text.stroke",
            "stylers": [
              {
                "color": "#ebe3cd"
              }
            ]
          },
          {
            "featureType": "transit.station",
            "elementType": "geometry",
            "stylers": [
              {
                "color": "#dfd2ae"
              }
            ]
          },
          {
            "featureType": "water",
            "elementType": "geometry.fill",
            "stylers": [
              {
                "color": "#b9d3c2"
              }
            ]
          },
          {
            "featureType": "water",
            "elementType": "labels.text.fill",
            "stylers": [
              {
                "color": "#92998d"
              }
            ]
          }
        ]
      });
      setMap(newMap);
      setMapLoaded(true);
      mapRef.current._google_map_instance = newMap;
    }
    return () => {
      setMapLoaded(false);
      if (mapRef.current && mapRef.current._google_map_instance) {
        delete mapRef.current._google_map_instance;
      }
    };
  }, [isGoogleMapsLoaded]);

  // Create or update markers when vehicles change
  useEffect(() => {
    // Guard: ensure Google Maps is available
    if (!isGoogleMapsLoaded || !window.google?.maps) {
      return;
    }

    if (!mapLoaded || !map) {
      markerObjectsRef.current.forEach(marker => marker.setMap(null));
      markerObjectsRef.current.clear();
      infoWindowsRef.current.forEach(infoWindow => infoWindow.close());
      infoWindowsRef.current.clear();
      customOverlaysRef.current.forEach(overlay => overlay.setMap(null));
      customOverlaysRef.current.clear();
      if (lastMapInstanceRef.current) lastMapInstanceRef.current = null;
      markerAnimationRefs.current.forEach(ref => { if (ref && ref.id) cancelAnimationFrame(ref.id); });
      markerAnimationRefs.current = [];
      return;
    }

    // If the map instance has changed, we need to clear and recreate all markers for the new map.
    if (map !== lastMapInstanceRef.current) {
      markerObjectsRef.current.forEach(marker => marker.setMap(null)); 
      markerObjectsRef.current.clear();
      infoWindowsRef.current.forEach(infoWindow => infoWindow.close());
      infoWindowsRef.current.clear();
      customOverlaysRef.current.forEach(overlay => overlay.setMap(null));
      customOverlaysRef.current.clear();
      lastMapInstanceRef.current = map; 
      markerAnimationRefs.current.forEach(ref => { if (ref && ref.id) cancelAnimationFrame(ref.id); });
      markerAnimationRefs.current = [];
    }

    // Clear markers that are not in the current filter
    if (activeFilter === "ambulances") {
      // Remove medecin and infirmier markers
      const keysToRemove = [];
      markerObjectsRef.current.forEach((marker, key) => {
        const keyStr = String(key);
        if (keyStr.startsWith('medecin-') || keyStr.startsWith('infirmier-')) {
          marker.setMap(null);
          const infoWindow = infoWindowsRef.current.get(key);
          if (infoWindow) {
            infoWindow.close();
            infoWindowsRef.current.delete(key);
          }
          const customOverlay = customOverlaysRef.current.get(key);
          if (customOverlay) {
            customOverlay.setMap(null);
            customOverlaysRef.current.delete(key);
          }
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach(key => markerObjectsRef.current.delete(key));
    } else if (activeFilter === "medecins") {
      // Remove vehicle and infirmier markers
      const keysToRemove = [];
      markerObjectsRef.current.forEach((marker, key) => {
        const keyStr = String(key);
        if (!keyStr.startsWith('medecin-')) {
          marker.setMap(null);
          const infoWindow = infoWindowsRef.current.get(key);
          if (infoWindow) {
            infoWindow.close();
            infoWindowsRef.current.delete(key);
          }
          const customOverlay = customOverlaysRef.current.get(key);
          if (customOverlay) {
            customOverlay.setMap(null);
            customOverlaysRef.current.delete(key);
          }
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach(key => markerObjectsRef.current.delete(key));
    } else if (activeFilter === "infirmiers") {
      // Remove vehicle and medecin markers
      const keysToRemove = [];
      markerObjectsRef.current.forEach((marker, key) => {
        const keyStr = String(key);
        if (!keyStr.startsWith('infirmier-')) {
          marker.setMap(null);
          const infoWindow = infoWindowsRef.current.get(key);
          if (infoWindow) {
            infoWindow.close();
            infoWindowsRef.current.delete(key);
          }
          const customOverlay = customOverlaysRef.current.get(key);
          if (customOverlay) {
            customOverlay.setMap(null);
            customOverlaysRef.current.delete(key);
          }
          keysToRemove.push(key);
        }
      });
      keysToRemove.forEach(key => markerObjectsRef.current.delete(key));
    }

    const newMarkerStateArray = [];
    const currentVehicleIds = new Set();
    const newAnimationRefs = [];

    const ambulanceMarkerIcon = {
      url: ambulanceIcon,
      scaledSize: new window.google.maps.Size(60, 50),
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(35, 30) 
    };

    // Show ambulances if filter is ambulances
    if (activeFilter === "ambulances") {
      filteredVehicles.forEach((vehicle) => {
        currentVehicleIds.add(vehicle.id);
        let marker = markerObjectsRef.current.get(vehicle.id);
        let animationRef = markerAnimationRefs.current.find(ref => ref && ref.vehicleId === vehicle.id);

        // Déterminer la couleur selon le statut
        const isEnIntervention = vehicle.statut && (
          vehicle.statut.toLowerCase().includes('intervention') || 
          vehicle.statut.toLowerCase().includes('mission') ||
          vehicle.statut.toLowerCase().includes('en cours')
        );
        const bgColor = isEnIntervention ? '#F6B100' : '#006133';

        if (!marker) {
          marker = new window.google.maps.Marker({
            position: vehicle.position,
            map: map,
            title: `${vehicle.nom} ${vehicle.prenom}`,
            icon: ambulanceMarkerIcon,
          });
          marker.addListener("click", () => {
            navigate(`/admin/vehicules/${vehicle.id}`);
          });
          markerObjectsRef.current.set(vehicle.id, marker);

          // Créer un overlay personnalisé au lieu d'une InfoWindow
          const contentString = `
            <div style="background-color: ${bgColor}; padding: 8px 12px; border-radius: 6px; color: white; font-family: Arial, sans-serif; font-size: 14px; font-weight: 600; display: inline-block; margin: 0; white-space: nowrap; pointer-events: none; user-select: none;">
              ${vehicle.imatricule}
            </div>
          `;
          const customOverlay = new CustomLabelOverlay(
            new window.google.maps.LatLng(vehicle.position.lat, vehicle.position.lng),
            contentString,
            map
          );
          customOverlay.setMap(map);
          customOverlaysRef.current.set(vehicle.id, customOverlay);
          animationRef = { vehicleId: vehicle.id, id: null };
        } else {
          // Mettre à jour le contenu et la position de l'overlay personnalisé
          const existingOverlay = customOverlaysRef.current.get(vehicle.id);
          if (existingOverlay) {
            const updatedContentString = `
              <div style="background-color: ${bgColor}; padding: 8px 12px; border-radius: 6px; color: white; font-family: Arial, sans-serif; font-size: 14px; font-weight: 600; display: inline-block; margin: 0; white-space: nowrap; pointer-events: none; user-select: none;">
                ${vehicle.imatricule}
              </div>
            `;
            existingOverlay.setContent(updatedContentString);
            existingOverlay.setPosition(new window.google.maps.LatLng(vehicle.position.lat, vehicle.position.lng));
          }
          if (!animationRef) {
            animationRef = { vehicleId: vehicle.id, id: null };
          }
        }
        
        newMarkerStateArray.push(marker);
        newAnimationRefs.push(animationRef);
      });
    }

    // Show medecins if filter is medecins
    if (activeFilter === "medecins") {
      const medecinMarkerIcon = {
        url: medecinIcon,
        scaledSize: new window.google.maps.Size(20, 20),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(15, 15)
      };

      medecins.forEach((medecin) => {
        if (!medecin.position || !medecin.position.lat || !medecin.position.lng) {
          return;
        }

        const markerId = `medecin-${medecin.id}`;
        let marker = markerObjectsRef.current.get(markerId);

        // Médecins considérés comme disponibles par défaut
        const bgColor = '#29BACD';

        if (!marker) {
          marker = new window.google.maps.Marker({
            position: medecin.position,
            map: map,
            title: `Dr. ${medecin.nom} ${medecin.prenom}`,
            icon: medecinMarkerIcon,
          });
          markerObjectsRef.current.set(markerId, marker);

          // Créer un overlay personnalisé au lieu d'une InfoWindow
          const contentString = `
            <div style="background-color: ${bgColor}; padding: 8px 12px; border-radius: 6px; color: white; font-family: Arial, sans-serif; font-size: 14px; font-weight: 600; display: inline-block; margin: 0; white-space: nowrap; pointer-events: none; user-select: none;">
              Dr. ${medecin.nom} ${medecin.prenom}
            </div>
          `;
          const customOverlay = new CustomLabelOverlay(
            new window.google.maps.LatLng(medecin.position.lat, medecin.position.lng),
            contentString,
            map
          );
          customOverlay.setMap(map);
          customOverlaysRef.current.set(markerId, customOverlay);
        } else {
          marker.setPosition(medecin.position);
          // Mettre à jour la position de l'overlay personnalisé
          const existingOverlay = customOverlaysRef.current.get(markerId);
          if (existingOverlay) {
            existingOverlay.setPosition(new window.google.maps.LatLng(medecin.position.lat, medecin.position.lng));
          }
        }

        newMarkerStateArray.push(marker);
      });
    }

    // Show infirmiers if filter is infirmiers
    if (activeFilter === "infirmiers") {
      const infirmierMarkerIcon = {
        url: infirmierIcon,
        scaledSize: new window.google.maps.Size(20, 20),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(15, 15)
      };

      infirmiers.forEach((infirmier) => {
        if (!infirmier.position || !infirmier.position.lat || !infirmier.position.lng) {
          return;
        }

        const markerId = `infirmier-${infirmier.id}`;
        let marker = markerObjectsRef.current.get(markerId);

        // Infirmiers considérés comme disponibles par défaut
        const bgColor = '#29BACD';

        if (!marker) {
          marker = new window.google.maps.Marker({
            position: infirmier.position,
            map: map,
            title: `${infirmier.nom} ${infirmier.prenom} - Infirmier`,
            icon: infirmierMarkerIcon,
          });
          markerObjectsRef.current.set(markerId, marker);

          // Créer un overlay personnalisé au lieu d'une InfoWindow
          const contentString = `
            <div style="background-color: ${bgColor}; padding: 8px 12px; border-radius: 6px; color: white; font-family: Arial, sans-serif; font-size: 14px; font-weight: 600; display: inline-block; margin: 0; white-space: nowrap; pointer-events: none; user-select: none;">
              ${infirmier.nom} ${infirmier.prenom}
            </div>
          `;
          const customOverlay = new CustomLabelOverlay(
            new window.google.maps.LatLng(infirmier.position.lat, infirmier.position.lng),
            contentString,
            map
          );
          customOverlay.setMap(map);
          customOverlaysRef.current.set(markerId, customOverlay);
        } else {
          marker.setPosition(infirmier.position);
          // Mettre à jour la position de l'overlay personnalisé
          const existingOverlay = customOverlaysRef.current.get(markerId);
          if (existingOverlay) {
            existingOverlay.setPosition(new window.google.maps.LatLng(infirmier.position.lat, infirmier.position.lng));
          }
        }

        newMarkerStateArray.push(marker);
      });
    }

    // Remove markers for vehicles that are no longer present (only for ambulances filter)
    if (activeFilter === "ambulances") {
      const oldAnimationRefs = [...markerAnimationRefs.current];
      markerObjectsRef.current.forEach((marker, vehicleId) => {
        const keyStr = String(vehicleId);
        // Only remove vehicle markers (not medecin or infirmier markers)
        if (!keyStr.startsWith('medecin-') && !keyStr.startsWith('infirmier-') && !currentVehicleIds.has(vehicleId)) {
          marker.setMap(null);
          markerObjectsRef.current.delete(vehicleId);
          const infoWindow = infoWindowsRef.current.get(vehicleId);
          if (infoWindow) {
            infoWindow.close();
            infoWindowsRef.current.delete(vehicleId);
          }
          const customOverlay = customOverlaysRef.current.get(vehicleId);
          if (customOverlay) {
            customOverlay.setMap(null);
            customOverlaysRef.current.delete(vehicleId);
          }
          const refToRemove = oldAnimationRefs.find(ref => ref && ref.vehicleId === vehicleId);
          if (refToRemove && refToRemove.id) {
            cancelAnimationFrame(refToRemove.id);
          }
        }
      });
      markerAnimationRefs.current = newAnimationRefs;
    }

    // Fit bounds to show all markers - Désactivé pour éviter le recentrage automatique
    // if (newMarkerStateArray.length > 0) {
    //   const bounds = new window.google.maps.LatLngBounds();
    //   newMarkerStateArray.forEach(marker => {
    //     const pos = marker.getPosition();
    //     if (pos) {
    //       bounds.extend(pos);
    //     }
    //   });
    //   if (!bounds.isEmpty()) {
    //     map.fitBounds(bounds);
    //   }
    // }

  }, [filteredVehicles, medecins, infirmiers, activeFilter, mapLoaded, map]); 

  // Animate marker positions when vehicles array indicates a change in position
  useEffect(() => {
    // Guard: ensure Google Maps is available
    if (!isGoogleMapsLoaded || !window.google?.maps) {
      return;
    }

    if (!mapLoaded || !map || !filteredVehicles.length || activeFilter !== "ambulances") return;

    filteredVehicles.forEach((vehicle, index) => {
      const marker = markerObjectsRef.current.get(vehicle.id);
      if (!marker) return;

      const oldPosition = marker.getPosition();
      const newGPosition = new window.google.maps.LatLng(vehicle.position.lat, vehicle.position.lng);

      const fromLat = oldPosition.lat();
      const fromLng = oldPosition.lng();
      const toLat = newGPosition.lat();
      const toLng = newGPosition.lng();

      // Si la position est la même ou très proche, ne pas animer
      if (Math.abs(fromLat - toLat) < 0.00001 && Math.abs(fromLng - toLng) < 0.00001) {
        return;
      }

      // Calculer la distance entre les points
      const distance = Math.sqrt(
        Math.pow(toLat - fromLat, 2) + Math.pow(toLng - fromLng, 2)
      );

      // Si la distance est trop grande, téléporter directement
      if (distance > 0.01) {
        marker.setPosition(newGPosition);
        // Mettre à jour la position de l'overlay personnalisé
        const customOverlay = customOverlaysRef.current.get(vehicle.id);
        if (customOverlay) {
          customOverlay.setPosition(newGPosition);
        }
        return;
      }

      // Animation fluide
      const duration = 1000; // 1 seconde
      const startTime = Date.now();
      const startLat = fromLat;
      const startLng = fromLng;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        const currentLat = startLat + (toLat - startLat) * easeOut;
        const currentLng = startLng + (toLng - startLng) * easeOut;
        
        marker.setPosition(new window.google.maps.LatLng(currentLat, currentLng));
        
        // Mettre à jour la position de l'overlay personnalisé pendant l'animation
        const customOverlay = customOverlaysRef.current.get(vehicle.id);
        if (customOverlay) {
          customOverlay.setPosition(new window.google.maps.LatLng(currentLat, currentLng));
        }
        
        if (progress < 1) {
          const animationId = requestAnimationFrame(animate);
          const ref = markerAnimationRefs.current.find(r => r && r.vehicleId === vehicle.id);
          if (ref) {
            ref.id = animationId;
          }
        }
      };

      const animationId = requestAnimationFrame(animate);
      const ref = markerAnimationRefs.current.find(r => r && r.vehicleId === vehicle.id);
      if (ref) {
        ref.id = animationId;
      }
    });
  }, [filteredVehicles, mapLoaded, map, activeFilter]);

  // Fetch users (medecins and infirmiers)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRes = await getUsers();
        const usersData = usersRes.data || [];
        setUsers(usersData);

        // Filter and get locations for medecins (exact role match: "médecin")
        const medecinsFiltered = usersData.filter(u => {
          const role = u.role || u.rôle;
          return role === 'médecin';
        });
        console.log('Medecins filtered:', medecinsFiltered.length);
        
        const medecinsData = await Promise.all(
          medecinsFiltered.map(async (user) => {
            const location = await fetchStaffLocation(user.id);
            console.log(`Medecin ${user.nom} ${user.prenom} location:`, location);
            return location ? { ...user, position: location } : null;
          })
        );
        const validMedecins = medecinsData.filter(Boolean);
        console.log('Valid medecins with location:', validMedecins.length);
        setMedecins(validMedecins);

        // Filter and get locations for infirmiers (exact role match: "infirmier")
        const infirmiersFiltered = usersData.filter(u => {
          const role = u.role || u.rôle;
          return role === 'infirmier';
        });
        console.log('Infirmiers filtered:', infirmiersFiltered.length);
        
        const infirmiersData = await Promise.all(
          infirmiersFiltered.map(async (user) => {
            const location = await fetchStaffLocation(user.id);
            console.log(`Infirmier ${user.nom} ${user.prenom} location:`, location);
            return location ? { ...user, position: location } : null;
          })
        );
        const validInfirmiers = infirmiersData.filter(Boolean);
        console.log('Valid infirmiers with location:', validInfirmiers.length);
        setInfirmiers(validInfirmiers);

        // Calculate stats (will be updated when vehicles are loaded)
        setStats(prev => ({
          ...prev,
          medecins: medecinsData.length,
          infirmiers: infirmiersData.length,
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
    const interval = setInterval(() => {
      fetchUsers();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch vehicles on mount and set up interval
  useEffect(() => {
    fetchVehicles();
    const interval = setInterval(() => {
      fetchVehicles();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Keep InfoWindows always open and hide close button and white background
  useEffect(() => {
    if (!mapLoaded || !map) return;

    const keepInfoWindowsOpen = () => {
      infoWindowsRef.current.forEach((infoWindow, key) => {
        const marker = markerObjectsRef.current.get(key);
        if (marker && marker.getMap()) {
          // Vérifier si l'InfoWindow est ouverte en vérifiant son contenu
          // Si elle n'est pas ouverte, la rouvrir
          try {
            if (!infoWindow.getMap()) {
              infoWindow.open(map, marker);
            }
          } catch (e) {
            // Si l'InfoWindow a été fermée, la rouvrir
            infoWindow.open(map, marker);
          }
        }
      });
      
      // Masquer le bouton X et le fond blanc à chaque vérification
      setTimeout(() => {
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (iframeDoc) {
              // Masquer le bouton de fermeture - approche agressive
              const selectors = [
                '.gm-ui-hover-effect', 
                '[aria-label*="Close"]', 
                '[aria-label*="close"]',
                '[aria-label*="Fermer"]',
                '[aria-label*="fermer"]',
                '[title*="Close"]', 
                '[title*="close"]',
                '[title*="Fermer"]',
                '[title*="fermer"]',
                'button[aria-label*="Close"]',
                'button[aria-label*="close"]',
                'button[aria-label*="Fermer"]',
                '.gm-style-iw-tc',
                'div[role="button"]',
                'button',
                '[role="button"]'
              ];
              
              // Masquer tous les éléments qui pourraient être le bouton X
              selectors.forEach(selector => {
                try {
                  const elements = iframeDoc.querySelectorAll(selector);
                  elements.forEach(el => {
                    const text = el.textContent || '';
                    const ariaLabel = el.getAttribute('aria-label') || '';
                    const title = el.getAttribute('title') || '';
                    const className = el.className || '';
                    
                    // Vérifier si c'est un bouton de fermeture
                    if (
                      text.includes('×') || 
                      text.includes('✕') || 
                      text.includes('X') ||
                      text.trim() === '×' ||
                      text.trim() === '✕' ||
                      text.trim() === 'X' ||
                      ariaLabel.toLowerCase().includes('close') ||
                      ariaLabel.toLowerCase().includes('fermer') ||
                      title.toLowerCase().includes('close') ||
                      title.toLowerCase().includes('fermer') ||
                      className.includes('gm-ui-hover-effect') ||
                      className.includes('gm-style-iw-tc')
                    ) {
                      el.style.display = 'none !important';
                      el.style.visibility = 'hidden !important';
                      el.style.opacity = '0 !important';
                      el.style.width = '0 !important';
                      el.style.height = '0 !important';
                      el.style.pointerEvents = 'none !important';
                      el.remove(); // Supprimer complètement l'élément
                    }
                  });
                } catch (e) {
                  // Ignorer les erreurs
                }
              });
              
              // Masquer aussi tous les boutons dans la zone de l'InfoWindow
              const infoWindowContainer = iframeDoc.querySelector('.gm-style-iw-c');
              if (infoWindowContainer) {
                const allButtons = infoWindowContainer.querySelectorAll('button, [role="button"]');
                allButtons.forEach(btn => {
                  const text = btn.textContent || '';
                  if (text.includes('×') || text.includes('✕') || text.includes('X') || text.trim() === '×' || text.trim() === '✕' || text.trim() === 'X') {
                    btn.style.display = 'none !important';
                    btn.style.visibility = 'hidden !important';
                    btn.remove();
                  }
                });
              }
              
              // Supprimer le fond blanc
              const infoWindowDiv = iframeDoc.querySelector('.gm-style-iw-d');
              if (infoWindowDiv) {
                infoWindowDiv.style.background = 'transparent';
                infoWindowDiv.style.padding = '0';
                infoWindowDiv.style.boxShadow = 'none';
              }
              
              const infoWindowParent = iframeDoc.querySelector('.gm-style-iw-c');
              if (infoWindowParent) {
                infoWindowParent.style.background = 'transparent';
                infoWindowParent.style.padding = '0';
                infoWindowParent.style.boxShadow = 'none';
              }
              
              const infoWindowBg = iframeDoc.querySelector('.gm-style-iw');
              if (infoWindowBg) {
                infoWindowBg.style.background = 'transparent';
              }
              
              const infoWindowTip = iframeDoc.querySelector('.gm-style-iw-t');
              if (infoWindowTip) {
                infoWindowTip.style.background = 'transparent';
              }
            }
          } catch (e) {
            // Ignorer les erreurs CORS
          }
        });
      }, 100);
    };

    const interval = setInterval(keepInfoWindowsOpen, 1000); // Vérifier toutes les secondes

    return () => clearInterval(interval);
  }, [mapLoaded, map, activeFilter, vehicles, medecins, infirmiers]);

  // If Google Maps is not loaded, show loading spinner
  if (!isGoogleMapsLoaded || !window.google?.maps) {
    return <LoadingSpinner message="Chargement de la carte..." size="md" />;
  }

  return (
    <Box p="20px" pt="120px" bg={bgColor} minH="100vh">
      {/* Header with Tabs */}
      <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
        <HStack spacing="10px" align="center" mb={activeFilter === "ambulances" ? "12px" : "0"}>
          <Text fontSize="13px" color="#656C7A" flex="1">
            Localisation des ambulances et zones d'intervention
          </Text>
          <HStack
            bg="#F9F9F9"
            border="1px solid"
            borderColor={borderColor}
            borderRadius="8px"
            p="3px"
            spacing="0"
          >
            <Button
              size="sm"
              h="30px"
              px="10px"
              fontSize="14px"
              fontWeight="500"
              bg={activeFilter === "ambulances" ? "white" : "transparent"}
              color={activeFilter === "ambulances" ? "#459CE9" : "#455879"}
              boxShadow={activeFilter === "ambulances" ? "0px 3px 4px 0px rgba(0,0,0,0.03)" : "none"}
              borderRadius="8px"
              onClick={() => setActiveFilter("ambulances")}
            >
              Ambulanciers
              <Badge
                ml="5px"
                bg={activeFilter === "ambulances" ? "#EFF6FF" : "white"}
                color={activeFilter === "ambulances" ? "#459CE9" : "#455879"}
                borderRadius="100px"
                px="3px"
                py="3px"
                fontSize="12px"
                fontWeight="600"
              >
                {normalizedAmbulanceSearch ? filteredVehicles.length : stats.ambulances}
              </Badge>
            </Button>
            <Button
              size="sm"
              h="30px"
              px="10px"
              fontSize="14px"
              fontWeight="500"
              bg={activeFilter === "medecins" ? "white" : "transparent"}
              color={activeFilter === "medecins" ? "#459CE9" : "#455879"}
              boxShadow={activeFilter === "medecins" ? "0px 3px 4px 0px rgba(0,0,0,0.03)" : "none"}
              borderRadius="6px"
              onClick={() => setActiveFilter("medecins")}
            >
              Médecins
              <Badge
                ml="5px"
                bg="white"
                color="#455879"
                borderRadius="100px"
                px="3px"
                py="3px"
                fontSize="12px"
                fontWeight="600"
              >
                {stats.medecins}
              </Badge>
            </Button>
            <Button
              size="sm"
              h="30px"
              px="10px"
              fontSize="14px"
              fontWeight="500"
              bg={activeFilter === "infirmiers" ? "white" : "transparent"}
              color={activeFilter === "infirmiers" ? "#459CE9" : "#455879"}
              boxShadow={activeFilter === "infirmiers" ? "0px 3px 4px 0px rgba(0,0,0,0.03)" : "none"}
              borderRadius="6px"
              onClick={() => setActiveFilter("infirmiers")}
            >
              Infirmiers
              <Badge
                ml="5px"
                bg="white"
                color="#455879"
                borderRadius="100px"
                px="3px"
                py="3px"
                fontSize="12px"
                fontWeight="600"
              >
                {stats.infirmiers}
              </Badge>
            </Button>
          </HStack>
        </HStack>
        {activeFilter === "ambulances" && (
          <Input
            placeholder="Rechercher par immatriculation "
            value={ambulanceSearch}
            onChange={(e) => setAmbulanceSearch(e.target.value)}
            h="38px"
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
            bg="white"
            fontSize="13px"
          />
        )}
      </Box>

      {/* Map Container */}
      <Box
        bg={bgCard}
        borderRadius="12px"
        border="1px solid"
        borderColor={borderColor}
        p="14px"
        boxShadow="0px 4px 20px 0px rgba(0,0,0,0.06)"
        position="relative"
        h="calc(100vh - 280px)"
      >
        {/* Map */}
        <Box
          ref={mapRef}
          w="100%"
          h="100%"
          borderRadius="10px"
          overflow="hidden"
        />

        {/* Legend */}
        <Box
          position="absolute"
          top="16px"
          left="16px"
          bg={bgCard}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="12px"
          p="20px"
          boxShadow="0px 4px 20px 0px rgba(0,0,0,0.06)"
          w="266px"
        >
          <Text fontSize="14px" fontWeight="500" color="#455879" mb="20px">
            Légende de la Carte
          </Text>
          <VStack spacing="10px" align="stretch">
            {activeFilter === "ambulances" ? (
              <>
                <HStack spacing="10px">
                  <Box position="relative" w="6px" h="6px">
                    <Box
                      position="absolute"
                      w="6px"
                      h="6px"
                      bg="rgba(0,97,51,0.1)"
                      borderRadius="50%"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                    >
                      <Box
                        w="6px"
                        h="6px"
                        bg="rgba(0,97,51,0.2)"
                        borderRadius="50%"
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      >
                        <Box
                          w="5px"
                          h="5px"
                          bg="#006133"
                          borderRadius="50%"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Text fontSize="13px" color="#455879">
                    Ambulances disponibles
                  </Text>
                </HStack>
                <HStack spacing="10px">
                  <Box position="relative" w="6px" h="6px">
                    <Box
                      position="absolute"
                      w="6px"
                      h="6px"
                      bg="rgba(246,177,0,0.1)"
                      borderRadius="50%"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                    >
                      <Box
                        w="6px"
                        h="6px"
                        bg="rgba(246,177,0,0.2)"
                        borderRadius="50%"
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      >
                        <Box
                          w="5px"
                          h="5px"
                          bg="#F6B100"
                          borderRadius="50%"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Text fontSize="13px" color="#455879">
                    En intervention
                  </Text>
                </HStack>
                <HStack spacing="10px">
                  <Box position="relative" w="6px" h="6px">
                    <Box
                      position="absolute"
                      w="6px"
                      h="6px"
                      bg="rgba(182,43,48,0.1)"
                      borderRadius="50%"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                    >
                      <Box
                        w="6px"
                        h="6px"
                        bg="rgba(182,43,48,0.2)"
                        borderRadius="50%"
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      >
                        <Box
                          w="5px"
                          h="5px"
                          bg="#B62B30"
                          borderRadius="50%"
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Text fontSize="13px" color="#455879">
                    Points de prise en charge
                  </Text>
                </HStack>
              </>
            ) : activeFilter === "medecins" ? (
              <HStack spacing="10px">
                <Box position="relative" w="6px" h="6px">
                  <Box
                    position="absolute"
                    w="6px"
                    h="6px"
                    bg="rgba(41,186,205,0.1)"
                    borderRadius="50%"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                  >
                    <Box
                      w="6px"
                      h="6px"
                      bg="rgba(41,186,205,0.2)"
                      borderRadius="50%"
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                    >
                      <Box
                        w="5px"
                        h="5px"
                        bg="#29BACD"
                        borderRadius="50%"
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      />
                    </Box>
                  </Box>
                </Box>
                <Text fontSize="13px" color="#455879">
                  Médecins disponibles
                </Text>
              </HStack>
            ) : (
              <HStack spacing="10px">
                <Box position="relative" w="6px" h="6px">
                  <Box
                    position="absolute"
                    w="6px"
                    h="6px"
                    bg="rgba(41,186,205,0.1)"
                    borderRadius="50%"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                  >
                    <Box
                      w="6px"
                      h="6px"
                      bg="rgba(41,186,205,0.2)"
                      borderRadius="50%"
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                    >
                      <Box
                        w="5px"
                        h="5px"
                        bg="#29BACD"
                        borderRadius="50%"
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                      />
                    </Box>
                  </Box>
                </Box>
                <Text fontSize="13px" color="#455879">
                  Infirmiers disponibles
                </Text>
              </HStack>
            )}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

const GpsMap = () => {
  return (
    <GoogleMapsLoader>
      <GpsMapContent />
    </GoogleMapsLoader>
  );
};

export default GpsMap;
